import React, { useEffect, useState } from "react";
import { Table, Tag, Popconfirm, message, Empty, Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import userService from "../../../services/user/user.service";
import { User } from "../../../types/user.types";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";

interface DeviceConflictUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface DeviceConflictLog {
  id: number;
  type: "device_taken" | "account_locked";
  deviceId: string;
  createdAt: string;
  user: DeviceConflictUser;
  conflictingUser?: DeviceConflictUser | null;
}

const typeLabels: Record<DeviceConflictLog["type"], string> = {
  device_taken: "Cihaz Başkasına Ait",
  account_locked: "Hesap Başka Cihaza Kilitli",
};

const typeColors: Record<DeviceConflictLog["type"], string> = {
  device_taken: "orange",
  account_locked: "red",
};

const DeviceConflictLogsAdmin: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<DeviceConflictLog[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [logsLoading, setLogsLoading] = useState<boolean>(true);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await userService.getUsers();
      setUsers(data);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const data = await userService.getDeviceConflictLogs();
      setLogs(data as DeviceConflictLog[]);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLogs();
  }, []);

  const handleReset = async (userId: number) => {
    setResettingUserId(userId);
    try {
      await userService.resetDevice(userId);
      message.success("Cihaz eşlemesi sıfırlandı.");
      await Promise.all([fetchUsers(), fetchLogs()]);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setResettingUserId(null);
    }
  };

  const renderUser = (u?: DeviceConflictUser | null) =>
    u ? (
      <div>
        <div>{u.firstName} {u.lastName}</div>
        <small className="text-muted">{u.email}</small>
      </div>
    ) : "-";

  const filteredUsers = users.filter((u) => {
    const q = searchText.trim().toLowerCase();
    if (!q) return true;
    return (
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.deviceId || "").toLowerCase().includes(q)
    );
  });

  const userColumns: ColumnsType<User> = [
    {
      title: "Çalışan",
      dataIndex: "firstName",
      render: (_: string, record: User) => (
        <div>
          <div>{record.firstName} {record.lastName}</div>
          <small className="text-muted">{record.email}</small>
        </div>
      ),
      sorter: (a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName),
    },
    {
      title: "Cihaz ID",
      dataIndex: "deviceId",
      render: (deviceId: string | null | undefined) =>
        deviceId
          ? <span title={deviceId}>{deviceId}</span>
          : <Tag>Eşleşmemiş</Tag>,
      sorter: (a, b) => (a.deviceId || "").localeCompare(b.deviceId || ""),
    },
    {
      title: "İşlem",
      render: (_: any, record: User) => (
        <Popconfirm
          title="Cihazı Sıfırla"
          description={`${record.firstName} ${record.lastName} kullanıcısının cihaz eşlemesini sıfırlamak istediğinize emin misiniz?`}
          okText="Evet"
          cancelText="Vazgeç"
          okButtonProps={{ style: { backgroundColor: "#7338a0" } }}
          onConfirm={() => handleReset(record.id)}
        >
          <button
            className="btn btn-sm btn-outline-primary"
            disabled={!record.deviceId || resettingUserId === record.id}
            title="Cihazı Sıfırla"
          >
            <i className="ti ti-reload me-1" />
            Sıfırla
          </button>
        </Popconfirm>
      ),
    },
  ];

  const logColumns: ColumnsType<DeviceConflictLog> = [
    {
      title: "Tarih",
      dataIndex: "createdAt",
      render: (v: string) => new Date(v).toLocaleString("tr-TR"),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Tip",
      dataIndex: "type",
      render: (type: DeviceConflictLog["type"]) => (
        <Tag color={typeColors[type]}>{typeLabels[type]}</Tag>
      ),
    },
    {
      title: "Giriş Yapmaya Çalışan",
      dataIndex: "user",
      render: (u: DeviceConflictUser) => renderUser(u),
    },
    {
      title: "Cihazın Asıl Sahibi",
      dataIndex: "conflictingUser",
      render: (u?: DeviceConflictUser | null) => renderUser(u),
    },
    {
      title: "Cihaz ID",
      dataIndex: "deviceId",
      render: (deviceId: string) => <span title={deviceId}>{deviceId}</span>,
    },
    {
      title: "İşlem",
      render: (_: any, record: DeviceConflictLog) => {
        const renderResetButton = (targetUser: DeviceConflictUser, label: string) => (
          <Popconfirm
            key={targetUser.id}
            title="Cihazı Sıfırla"
            description={`${targetUser.firstName} ${targetUser.lastName} kullanıcısının cihaz eşlemesini sıfırlamak istediğinize emin misiniz?`}
            okText="Evet"
            cancelText="Vazgeç"
            okButtonProps={{ style: { backgroundColor: "#7338a0" } }}
            onConfirm={() => handleReset(targetUser.id)}
          >
            <button
              className="btn btn-sm btn-outline-primary"
              disabled={resettingUserId === targetUser.id}
              title={`Cihazı Sıfırla (${targetUser.firstName} ${targetUser.lastName})`}
            >
              <i className="ti ti-reload me-1" />
              {label}
            </button>
          </Popconfirm>
        );

        // "Cihaz başkasına ait" durumunda asıl sorun cihazın hâlâ kayıtlı olduğu kişide,
        // ama girmeye çalışanın kendi hesabı da AYRICA başka bir cihaza kilitli olabilir —
        // bileşik durumlarda admin ikisini de buradan tek tek sıfırlayabilsin diye iki buton.
        if (record.type === "device_taken" && record.conflictingUser) {
          return (
            <div className="d-flex gap-2">
              {renderResetButton(record.conflictingUser, "Cihaz Sahibi")}
              {renderResetButton(record.user, "Giriş Yapan")}
            </div>
          );
        }

        return renderResetButton(record.user, "Sıfırla");
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1">Cihaz ID Sıfırlama</h5>
            <p className="text-muted mb-0">
              Tüm çalışanların hangi cihaza eşleştiğini buradan görüp, gerektiğinde sıfırlayabilirsiniz.
            </p>
          </div>
          <Input.Search
            placeholder="İsim, e-posta veya cihaz ID ara..."
            style={{ maxWidth: 320 }}
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="card mb-4">
          <div className="card-body">
            <Table
              rowKey="id"
              columns={userColumns}
              dataSource={filteredUsers}
              loading={usersLoading}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: <Empty description="Çalışan bulunamadı" /> }}
            />
          </div>
        </div>

        <h6 className="mb-2">Son Cihaz Çakışmaları</h6>
        <p className="text-muted">
          Bir çalışan, kayıtlı olmayan/başkasına ait bir cihazdan giriş yapmaya çalıştığında burada listelenir.
        </p>
        <div className="card">
          <div className="card-body">
            <Table
              rowKey="id"
              columns={logColumns}
              dataSource={logs}
              loading={logsLoading}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: <Empty description="Kayıtlı cihaz çakışması yok" /> }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceConflictLogsAdmin;
